/**
 * Individual mesh component for the 3D scene
 */

import { useMemo, useRef, useState, useEffect, memo } from "react";
import { ThreeEvent } from "@react-three/fiber";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import type { MeshData } from "../types";
import { isGlbMesh } from "../types";
import { ROBOT_MESH_FLAG } from "./arQuickLook";

// Create a shared DRACOLoader instance
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath("https://www.gstatic.com/draco/versioned/decoders/1.5.7/");

// Global GLB cache: deduplicates concurrent URL fetch+parse work.
// Key = content-addressed URL, value = parsed sub-meshes.
interface CachedGlbEntry {
  geometry: THREE.BufferGeometry;
  material: THREE.Material | THREE.Material[] | null;
}
type CachedGlb = CachedGlbEntry[];
const glbCache = new Map<string, Promise<CachedGlb>>();

// A .glb file starts with the ASCII magic "glTF".
function hasGlbMagic(buffer: ArrayBuffer): boolean {
  if (buffer.byteLength < 4) return false;
  const head = new Uint8Array(buffer, 0, 4);
  return head[0] === 0x67 && head[1] === 0x6c && head[2] === 0x54 && head[3] === 0x46;
}

// Selection / hover / collision / preview tint the part rather than repaint
// it: the material is cloned and given an emissive glow, so its own colour,
// roughness and maps -- and therefore the shading that tells you the shape --
// survive. Building a fresh MeshStandardMaterial from the flat server colour
// instead is what made a selected or colliding part read as a solid
// silhouette, and a previewed one read as a grey blob (the server sends no
// colour for a GLB part, so that path fell through to the [128,128,128]
// placeholder).
interface TintSpec {
  color: number;
  intensity: number;
  /** Present only for a tint that also makes the part see-through. */
  opacity?: number;
}

const TINT_EMISSIVE: Record<"collision" | "selection" | "highlight" | "preview", TintSpec> = {
  collision: { color: 0x8a0e0e, intensity: 1.4 },
  selection: { color: 0x0e5d1e, intensity: 1.0 },
  highlight: { color: 0x0e3d80, intensity: 1.0 },
  // Faint, because the point of the preview is to read the module itself.
  // The transparency is what says "not committed yet"; the warm cast only
  // has to separate it from the parts that are.
  preview: { color: 0x6b3a05, intensity: 0.7, opacity: 0.6 },
};

type TintKind = keyof typeof TINT_EMISSIVE;

// Long enough not to fire on a deliberate tap, short enough to feel like a
// press rather than a hang.
const LONG_PRESS_DURATION_MS = 550;
const LONG_PRESS_MOVE_TOLERANCE_PX = 10;

export function tintedMaterial(
  base: THREE.Material | THREE.Material[] | null,
  kind: TintKind,
  fallback: THREE.Material
): THREE.Material {
  const source = (Array.isArray(base) ? base[0] : base) ?? fallback;
  const cloned = source.clone();
  const { color, intensity, opacity } = TINT_EMISSIVE[kind];
  if ("emissive" in cloned) {
    const glowing = cloned as THREE.MeshStandardMaterial;
    glowing.emissive = new THREE.Color(color);
    glowing.emissiveIntensity = intensity;
  } else if ("color" in cloned) {
    // a material with no emissive channel can only be blended towards the tint
    const flat = cloned as THREE.MeshBasicMaterial;
    flat.color = flat.color.clone().lerp(new THREE.Color(color), 0.6);
  }
  if (opacity !== undefined) {
    cloned.transparent = true;
    cloned.opacity = opacity;
  }
  return cloned;
}

function cloneMaterial(mat: THREE.Material | THREE.Material[] | null): THREE.Material | THREE.Material[] | null {
  if (!mat) return null;
  if (Array.isArray(mat)) return mat.map((m) => m.clone());
  return mat.clone();
}
import {
  useViewerStore,
  useShowConnectionPreview,
  usePreviewMeshTransforms,
  useMeshToInstance,
} from "../store";

interface SceneMeshProps {
  mesh: MeshData;
  isHighlighted?: boolean;
  isColliding?: boolean;
}

// Type for the startDrag function exposed via window
type StartDragFn = (instanceId: string, meshUuid: string, event: PointerEvent) => void;

export const SceneMesh = memo(function SceneMesh({ mesh, isHighlighted = false, isColliding = false }: SceneMeshProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const groupRef = useRef<THREE.Group>(null);
  const selectedMeshUuid = useViewerStore((state) => state.selectedMeshUuid);
  const setSelectedMesh = useViewerStore((state) => state.setSelectedMesh);
  const meshToInstance = useMeshToInstance();

  // Preview state
  const showConnectionPreview = useShowConnectionPreview();
  const previewMeshTransforms = usePreviewMeshTransforms();

  const isSelected = selectedMeshUuid === mesh.uuid;

  // Local drag offset: only re-render when THIS mesh's instance is part of
  // the drag group (dragged instance + descendants). Non-dragged meshes always
  // get 0 (stable primitive), avoiding unnecessary re-renders.
  const meshInstanceId = mesh.instance_id || meshToInstance[mesh.uuid];
  const isBeingDragged = useViewerStore(
    (state) => meshInstanceId !== undefined && state.draggingInstanceIds[meshInstanceId] === true
  );
  const dragOffsetX = useViewerStore(
    (state) => (meshInstanceId !== undefined && state.draggingInstanceIds[meshInstanceId]) ? state.dragOffset[0] : 0
  );
  const dragOffsetY = useViewerStore(
    (state) => (meshInstanceId !== undefined && state.draggingInstanceIds[meshInstanceId]) ? state.dragOffset[1] : 0
  );
  const dragOffsetZ = useViewerStore(
    (state) => (meshInstanceId !== undefined && state.draggingInstanceIds[meshInstanceId]) ? state.dragOffset[2] : 0
  );

  // Check if this mesh has a preview transform (backend calculates which meshes to preview)
  const previewTransform = showConnectionPreview ? previewMeshTransforms[mesh.uuid] : null;
  const isPreviewMesh = previewTransform !== null && previewTransform !== undefined;

  // State for GLB-loaded sub-meshes (geometry + material pairs)
  const [glbEntries, setGlbEntries] = useState<CachedGlbEntry[]>([]);

  // Load GLB geometry/material from URL and share work across concurrent mounts.
  const glbUrl = mesh.glb_url;
  useEffect(() => {
    let isUnmounted = false;

    if (!glbUrl) {
      setGlbEntries([]);
      return () => {
        isUnmounted = true;
      };
    }

    const key = glbUrl;
    let glbPromise = glbCache.get(key);

    if (!glbPromise) {
      let resolveShared: (entries: CachedGlb) => void = () => undefined;
      let rejectShared: (error: unknown) => void = () => undefined;

      // Register synchronously so concurrent mounts of the same URL cannot
      // launch duplicate fetch/parse work.
      glbPromise = new Promise<CachedGlb>((resolve, reject) => {
        resolveShared = resolve;
        rejectShared = reject;
      });
      glbCache.set(key, glbPromise);

      void (async () => {
        try {
          const response = await fetch(glbUrl);
          if (!response.ok) {
            throw new Error(`HTTP ${response.status} while fetching ${glbUrl}`);
          }
          const arrayBuffer = await response.arrayBuffer();

          // A misrouted /glb/ path reaches the SPA fallback, which answers 200
          // with index.html. GLTFLoader would then try to read it as a .gltf
          // JSON document and fail with "Unexpected token '<'", which says
          // nothing about the real problem. Check the container magic instead.
          if (!hasGlbMagic(arrayBuffer)) {
            const contentType = response.headers.get("content-type") ?? "unknown";
            throw new Error(
              `${glbUrl} did not return a GLB (content-type ${contentType}, ` +
              `${arrayBuffer.byteLength} bytes). The path is probably not routed ` +
              `to the assets server.`
            );
          }

          const entries = await new Promise<CachedGlb>((resolve, reject) => {
            const loader = new GLTFLoader();
            loader.setDRACOLoader(dracoLoader);

            const normalizeMaterial = (mat: THREE.Material): THREE.Material => {
              const cloned = mat.clone();
              cloned.side = THREE.DoubleSide;
              if (cloned instanceof THREE.MeshStandardMaterial) {
                cloned.metalness = Math.min(cloned.metalness, 0.1);
                cloned.roughness = Math.max(cloned.roughness, 0.5);
              }
              return cloned;
            };

            const handleLoad = (gltf: { scene: THREE.Object3D }) => {
              const parsedEntries: CachedGlbEntry[] = [];
              gltf.scene.traverse((child) => {
                if (child instanceof THREE.Mesh) {
                  const geo = child.geometry.clone();
                  let mat: THREE.Material | THREE.Material[] | null;
                  if (Array.isArray(child.material)) {
                    mat = child.material.map(normalizeMaterial);
                  } else {
                    mat = normalizeMaterial(child.material);
                  }
                  parsedEntries.push({ geometry: geo, material: mat });
                }
              });
              resolve(parsedEntries);
            };

            const handleError = (error: unknown) => {
              reject(error);
            };

            try {
              loader.parse(arrayBuffer, "", handleLoad, handleError);
            } catch (error) {
              handleError(error);
            }
          });

          if (entries.length === 0) {
            glbCache.delete(key);
            resolveShared([]);
            return;
          }

          resolveShared(entries);
        } catch (error) {
          glbCache.delete(key);
          console.error("Error loading GLB:", error);
          rejectShared(error);
        }
      })();
    }

    glbPromise
      .then((entries) => {
        if (isUnmounted || entries.length === 0) return;
        setGlbEntries(entries.map((e) => ({ geometry: e.geometry.clone(), material: cloneMaterial(e.material) })));
      })
      .catch(() => {
        // Error is logged in handleError; this prevents unhandled rejection noise.
      });

    return () => {
      isUnmounted = true;
    };
  }, [glbUrl]);

  // Create geometry from vertices and faces (for non-GLB meshes)
  const legacyGeometry = useMemo(() => {
    if (isGlbMesh(mesh)) {
      return null;
    }

    const geo = new THREE.BufferGeometry();

    // Flatten vertices
    const vertices = mesh.vertices ?? [];
    const faces = mesh.faces ?? [];
    const positions = new Float32Array(vertices.flat());
    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));

    // Flatten faces
    const indices = new Uint32Array(faces.flat());
    geo.setIndex(new THREE.BufferAttribute(indices, 1));

    // Compute normals
    geo.computeVertexNormals();

    return geo;
  }, [mesh]);

  // Use either first GLB geometry or legacy geometry (for material/selection logic)
  const geometry = (glbEntries.length > 0 ? glbEntries[0].geometry : null) ?? legacyGeometry;

  // Get color with fallback for GLB meshes without color override
  const meshColor: [number, number, number] = mesh.color ?? [128, 128, 128];

  // Create material with color
  const material = useMemo(() => {
    const [r, g, b] = meshColor;
    return new THREE.MeshStandardMaterial({
      color: new THREE.Color(r / 255, g / 255, b / 255),
      opacity: mesh.opacity,
      transparent: mesh.opacity < 1,
      side: THREE.DoubleSide,
    });
  }, [meshColor, mesh.opacity]);

  // Convert quaternion to Three.js format (xyzw)
  // For preview meshes, use pre-calculated quaternion from backend
  const finalQuaternion = useMemo(() => {
    if (isPreviewMesh && previewTransform) {
      const [x, y, z, w] = previewTransform.quaternion;
      return new THREE.Quaternion(x, y, z, w);
    }
    const [x, y, z, w] = mesh.quaternion;
    return new THREE.Quaternion(x, y, z, w);
  }, [mesh.quaternion, isPreviewMesh, previewTransform]);

  // For preview meshes, use pre-calculated position from backend
  // For dragged meshes, apply the client-side drag offset for instant feedback
  const finalPosition: [number, number, number] = useMemo(() => {
    if (isPreviewMesh && previewTransform) {
      return previewTransform.position;
    }
    if (isBeingDragged) {
      return [
        mesh.position[0] + dragOffsetX,
        mesh.position[1] + dragOffsetY,
        mesh.position[2] + dragOffsetZ,
      ];
    }
    return mesh.position;
  }, [mesh.position, isPreviewMesh, previewTransform, isBeingDragged, dragOffsetX, dragOffsetY, dragOffsetZ]);

  // Press and hold a link to hand it to IK. Held rather than tapped because a
  // tap already means select-and-drag, and touch has no second button to
  // borrow. Cancelled by movement, so a drag of the module never becomes one.
  const armLongPress = (
    event: ThreeEvent<PointerEvent>,
    linkName: string,
    instanceId: string | undefined
  ) => {
    const origin = { x: event.nativeEvent.clientX, y: event.nativeEvent.clientY };
    let timer: number | null = null;

    const cleanup = () => {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", cleanup);
      document.removeEventListener("pointercancel", cleanup);
    };

    const onMove = (moveEvent: PointerEvent) => {
      const travelled = Math.hypot(moveEvent.clientX - origin.x, moveEvent.clientY - origin.y);
      if (travelled > LONG_PRESS_MOVE_TOLERANCE_PX) {
        cleanup();
      }
    };

    timer = window.setTimeout(() => {
      timer = null;
      cleanup();
      window.dispatchEvent(
        new CustomEvent("mesh-long-press", { detail: { linkName, instanceId } })
      );
    }, LONG_PRESS_DURATION_MS);

    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", cleanup);
    document.addEventListener("pointercancel", cleanup);
  };

  const handlePointerDown = (event: ThreeEvent<PointerEvent>) => {
    // Exclude LineSegments (edge highlights on selection) from click detection
    // because they can be hit within the Raycaster's threshold (default 1m)
    if (event.object.type === "LineSegments" || event.object.type === "Line") {
      return;
    }

    event.stopPropagation();

    // Get instance ID for this mesh
    const instanceId = mesh.instance_id || meshToInstance[mesh.uuid];

    // Placement mode owns every press: arming IK from under a module the user
    // is about to drop something onto would fight for the same gesture.
    if (mesh.link_name && !useViewerStore.getState().pendingPlacementModule) {
      armLongPress(event, mesh.link_name, instanceId);
    }

    if (instanceId) {
      // Start drag via the global function (also handles selection)
      const startDrag = (window as { __startDrag?: StartDragFn }).__startDrag;
      if (startDrag) {
        startDrag(instanceId, mesh.uuid, event.nativeEvent);
      }
    } else {
      // No instance, just select the mesh
      setSelectedMesh(mesh.uuid);
    }
  };

  const handleContextMenu = (event: ThreeEvent<MouseEvent>) => {
    event.stopPropagation();
    const instanceId = mesh.instance_id || meshToInstance[mesh.uuid];
    if (instanceId) {
      window.dispatchEvent(
        new CustomEvent("mesh-context-menu", {
          detail: {
            instanceId,
            clientX: event.nativeEvent.clientX,
            clientY: event.nativeEvent.clientY,
          },
        })
      );
    }
  };

  // When the server sends an explicit color override (e.g. URDF material color),
  // clone the GLB material and replace only the color so that normals,
  // roughness, metalness etc. are preserved for proper lighting/shading.
  const hasServerColor = mesh.color != null;
  const colorOverrideMaterial = useMemo(() => {
    if (!hasServerColor) return null;
    const [r, g, b] = meshColor;
    const serverColor = new THREE.Color(r / 255, g / 255, b / 255);
    // Clone the GLB material if available, otherwise create a new one
    const baseMat = (glbEntries.length > 0 && glbEntries[0].material)
      ? (Array.isArray(glbEntries[0].material)
        ? glbEntries[0].material[0].clone()
        : glbEntries[0].material.clone())
      : new THREE.MeshStandardMaterial({ side: THREE.DoubleSide });
    if (baseMat instanceof THREE.MeshStandardMaterial) {
      baseMat.color = serverColor;
    }
    return baseMat;
  }, [hasServerColor, meshColor, glbEntries]);

  // Determine which material to use for the primary (or single) mesh
  // Priority: preview > collision > selection > highlight > server color > GLB material > default
  // Create selection material with green emissive glow (replaces EdgesGeometry lines)
  // What a tint sits on top of: the server colour override when there is one,
  // otherwise the part's own GLB material.
  const untintedMaterial = useMemo(() => {
    if (hasServerColor && colorOverrideMaterial) return colorOverrideMaterial;
    if (glbEntries.length > 0 && glbEntries[0].material) {
      const own = glbEntries[0].material;
      return Array.isArray(own) ? own[0] : own;
    }
    return material;
  }, [hasServerColor, colorOverrideMaterial, glbEntries, material]);

  // The preview is a tint like any other: cloned from what the part already
  // wears, so it keeps its colour and shading and only turns see-through.
  const previewMaterial = useMemo(() => {
    if (!isPreviewMesh) return null;
    return tintedMaterial(untintedMaterial, "preview", material);
  }, [isPreviewMesh, untintedMaterial, material]);

  const selectionMaterial = useMemo(() => {
    if (!isSelected) return null;
    return tintedMaterial(untintedMaterial, "selection", material);
  }, [isSelected, untintedMaterial, material]);

  // Create highlight material with blue emissive glow
  const highlightMaterial = useMemo(() => {
    if (!isHighlighted) return null;
    return tintedMaterial(untintedMaterial, "highlight", material);
  }, [isHighlighted, untintedMaterial, material]);

  // Create collision material with red emissive glow
  const collisionMaterial = useMemo(() => {
    if (!isColliding) return null;
    return tintedMaterial(untintedMaterial, "collision", material);
  }, [isColliding, untintedMaterial, material]);

  // Which tint the part is wearing, if any. Preview wins: while a candidate
  // is being posed, saying where it would land matters more than saying it is
  // also selected -- which it always is, because posing it requires selecting
  // it first.
  const tintKind: TintKind | null = isPreviewMesh
    ? "preview"
    : isColliding
      ? "collision"
      : isSelected
        ? "selection"
        : isHighlighted
          ? "highlight"
          : null;

  // One clone per sub-mesh, held across renders. A jog re-renders this
  // component on every preview tick, and cloning a material per part per tick
  // would allocate for the length of the gesture.
  const tintedGlbMaterials = useMemo(() => {
    if (!tintKind) return null;
    return glbEntries.map((entry) => {
      const own = (Array.isArray(entry.material) ? entry.material[0] : entry.material) ?? material;
      return tintedMaterial(
        hasServerColor && colorOverrideMaterial ? colorOverrideMaterial : own,
        tintKind,
        material
      );
    });
  }, [tintKind, glbEntries, hasServerColor, colorOverrideMaterial, material]);

  const activeMaterial = useMemo(() => {
    if (isPreviewMesh && previewMaterial) return previewMaterial;
    if (isColliding && collisionMaterial) return collisionMaterial;
    if (isSelected && selectionMaterial) return selectionMaterial;
    if (isHighlighted && highlightMaterial) return highlightMaterial;
    if (hasServerColor && colorOverrideMaterial) return colorOverrideMaterial;
    if (glbEntries.length > 0 && glbEntries[0].material) return glbEntries[0].material;
    return material;
  }, [
    isPreviewMesh,
    previewMaterial,
    isColliding,
    collisionMaterial,
    isSelected,
    selectionMaterial,
    isHighlighted,
    highlightMaterial,
    hasServerColor,
    colorOverrideMaterial,
    glbEntries,
    material,
  ]);

  // Don't render if geometry is not ready
  if (!geometry && glbEntries.length === 0) {
    return null;
  }

  // Multi-mesh GLB: render all sub-meshes in a group
  if (glbEntries.length > 1) {
    // A tint is applied per sub-mesh, from that sub-mesh's own material.
    // Handing every part one shared material was the other half of the "it goes
    // flat when selected" problem: the group lost its internal variation too --
    // and the preview kept doing exactly that until it became a tint like the
    // rest.
    return (
      <group
        ref={groupRef}
        position={finalPosition}
        quaternion={finalQuaternion}
        userData={{ [ROBOT_MESH_FLAG]: true, glbUrl: mesh.glb_url ?? null, linkName: mesh.link_name ?? null, instanceId: mesh.instance_id ?? null }}
        onPointerDown={handlePointerDown}
        onContextMenu={handleContextMenu}
      >
        {glbEntries.map((entry, idx) => {
          const own = (Array.isArray(entry.material) ? entry.material[0] : entry.material) ?? material;
          let applied: THREE.Material = own;
          if (tintedGlbMaterials) {
            applied = tintedGlbMaterials[idx];
          } else if (hasServerColor && colorOverrideMaterial) {
            applied = colorOverrideMaterial;
          }
          return <mesh key={idx} geometry={entry.geometry} material={applied} />;
        })}
      </group>
    );
  }

  // Single mesh (GLB with one sub-mesh or legacy vertices/faces)
  if (!geometry) return null;

  return (
    <mesh
      ref={meshRef}
      geometry={geometry}
      material={activeMaterial}
      position={finalPosition}
      quaternion={finalQuaternion}
      userData={{ [ROBOT_MESH_FLAG]: true, glbUrl: mesh.glb_url ?? null, linkName: mesh.link_name ?? null, instanceId: mesh.instance_id ?? null }}
      onPointerDown={handlePointerDown}
      onContextMenu={handleContextMenu}
    />
  );
});
